import React, { useEffect, useRef, useState } from 'react';
import { X as XMarkIcon, ChevronUp } from 'lucide-react';
import { getRandomColor } from '../utils/common';
import { broadcaster, useBroadcastedState, setBroadcastHookState } from '../utils/events';
import { createPopupsContainer } from './popups';
import "../css/chat.css"

/* args: window_id, is_minimized */
const EVENT_SET_CHAT_WINDOW_MINIMIZED = "set_chat_window_minimized"
const STATE_CHAT_WINDOWS_VERSION = "chat_windows_version"
const CHAT_DOCK_VIEW_KEY = "__minimized_chats_dock"

const CHAT_WINDOW_DEFAULT_WIDTH = 450;
const CHAT_WINDOW_MIN_WIDTH = 360;
/* border/padding + gap between windows */
const CHAT_WINDOW_EXTRA_WIDTH = 20;
const CHAT_MINIMIZED_BAR_WIDTH = 228;
const CHAT_MORE_BUTTON_WIDTH = 96;
const CHAT_WINDOW_WIDTH_STORAGE_KEY = "chat_window_width";

/* window_id -> width, in the order they were opened/maximized, oldest first */
const maximized_chat_windows = new Map();
/* window_id -> {window_id, session_data, unseen_count, maximize, close}, oldest minimized first */
const minimized_chat_windows = new Map();
let chat_windows_version = 0;

function notifyChatWindowsChanged(){
    setBroadcastHookState(STATE_CHAT_WINDOWS_VERSION, ++chat_windows_version);
}

function openChatWindowsWidth(){
    return Array.from(maximized_chat_windows.values()).reduce((total, width) => total + width + CHAT_WINDOW_EXTRA_WIDTH, 0);
}

/* minimize oldest open windows so the remaining ones fit on screen, newest is always kept */
function fitMaximizedChatWindows(viewport_width){
    if(!viewport_width) return;
    let used_width = CHAT_MORE_BUTTON_WIDTH;
    Array.from(maximized_chat_windows).reverse().forEach(([window_id, width], i) => {
        used_width += width + CHAT_WINDOW_EXTRA_WIDTH;
        i > 0 && used_width > viewport_width
            && broadcaster.broadcast_event(EVENT_SET_CHAT_WINDOW_MINIMIZED, window_id, true);
    });
}

function maxChatWindowWidth(viewport_width){
    return Math.max(CHAT_WINDOW_MIN_WIDTH, viewport_width - CHAT_MORE_BUTTON_WIDTH - CHAT_WINDOW_EXTRA_WIDTH);
}

function maximizeChatWindow(window_id){
    broadcaster.broadcast_event(EVENT_SET_CHAT_WINDOW_MINIMIZED, window_id, false);
}

/* keys of chat windows in the container, excluding the minimized chats bar */
function chatWindowKeys(container){
    return Object.keys(container.views).filter((key) => key !== CHAT_DOCK_VIEW_KEY);
}

/* minimized state and width of a chat window, registered globally so open windows always fit on screen */
function useChatWindowMinimized({window_id, session_data, session_data_list, user_id, onClose}){
    const [is_minimized, setIsMinimized] = useState(false);
    const [ViewPort] = useBroadcastedState("viewport");
    const viewport_width = ViewPort?.WIDTH || window.innerWidth;
    const [width, setWidth] = useState(
        () => Math.min(
            Number(localStorage.getItem(CHAT_WINDOW_WIDTH_STORAGE_KEY)) || CHAT_WINDOW_DEFAULT_WIDTH,
            maxChatWindowWidth(viewport_width)
        )
    );

    /* drag the left edge to resize, last width is remembered for new windows */
    const startResize = (evt) => {
        evt.preventDefault();
        const start_x = evt.clientX;
        const start_width = width;
        const max_width = maxChatWindowWidth(viewport_width);
        let new_width = start_width;
        const onMove = (e) => {
            new_width = Math.round(Math.min(max_width, Math.max(CHAT_WINDOW_MIN_WIDTH, start_width + start_x - e.clientX)));
            setWidth(new_width);
        };
        const onUp = () => {
            document.removeEventListener("pointermove", onMove);
            document.removeEventListener("pointerup", onUp);
            document.body.style.userSelect = "";
            localStorage.setItem(CHAT_WINDOW_WIDTH_STORAGE_KEY, new_width);
            if(maximized_chat_windows.has(window_id)){
                maximized_chat_windows.set(window_id, new_width);
                fitMaximizedChatWindows(viewport_width);
                notifyChatWindowsChanged();
            }
        };
        document.body.style.userSelect = "none";
        document.addEventListener("pointermove", onMove);
        document.addEventListener("pointerup", onUp);
    };

    useEffect(
        () => {
            if(!window_id) return;
            const setMinimized = (_window_id, minimized) => {_window_id === window_id && setIsMinimized(minimized)};
            broadcaster.add_event_listener(EVENT_SET_CHAT_WINDOW_MINIMIZED, setMinimized);
            return () => broadcaster.remove_event_listener(EVENT_SET_CHAT_WINDOW_MINIMIZED, setMinimized);
        }, []
    );

    useEffect(
        () => {
            if(!window_id) return;
            if(is_minimized){
                minimized_chat_windows.set(window_id, {
                    window_id, session_data, unseen_count: 0,
                    maximize: () => setIsMinimized(false),
                    close: () => onClose?.(),
                });
            } else {
                maximized_chat_windows.set(window_id, width);
                fitMaximizedChatWindows(viewport_width);
            }
            notifyChatWindowsChanged();
            return () => {
                minimized_chat_windows.delete(window_id);
                maximized_chat_windows.delete(window_id);
                notifyChatWindowsChanged();
            }
        }, [is_minimized]
    );

    /* keep minimized entry updated and count new messages while minimized */
    useEffect(
        () => {
            const entry = is_minimized && minimized_chat_windows.get(window_id);
            if(!entry) return;
            if(entry.session_data !== session_data){
                entry.session_data = session_data;
                notifyChatWindowsChanged();
            }
            const event_ids = (session_data_list || []).map((_session_data) => "im:" + _session_data.session._id);
            if(!event_ids.length) return;
            /* not returning true, so the message is still treated as unhandled (ping/toast) */
            const onMessage = (msg) => {
                if(!msg?.src_id || msg.src_id == user_id) return;
                entry.unseen_count += 1;
                notifyChatWindowsChanged();
            };
            broadcaster.add_event_listener(event_ids, onMessage);
            return () => broadcaster.remove_event_listener(event_ids, onMessage);
        }, [is_minimized, session_data, session_data_list]
    );

    return {is_minimized, setIsMinimized, width: Math.min(width, maxChatWindowWidth(viewport_width)), startResize};
}

function minimizedChatTitle(session_data){
    return (session_data?.session?.title || "").replace(/^whatsapp\s*/i, "").trim();
}

function MinimizedChatItem({entry, className}){
    const title = entry.session_data ? minimizedChatTitle(entry.session_data) : "Loading...";
    const image = entry.session_data?.session?.image;
    return (
        <div className={className} title={title} onClick={entry.maximize}>
            <div className='chat-min-avatar' style={{backgroundColor: getRandomColor(title)}}>
                {image ? <img src={image.url || image} alt={title} /> : (title[0] || "?").toUpperCase()}
            </div>
            <div className='chat-min-title'>{title}</div>
            {entry.unseen_count ? <div className='chat-min-badge'>{entry.unseen_count}</div> : null}
            <XMarkIcon size={16} className='chat-min-close'
                onClick={(evt) => {evt.stopPropagation(); entry.close()}}
            />
        </div>
    );
}

/* collapsed title bars of minimized chats, left of open windows, overflow goes into "+N more" */
function MinimizedChatsBar({getBarClassName}){
    const [ViewPort] = useBroadcastedState("viewport");
    useBroadcastedState(STATE_CHAT_WINDOWS_VERSION); // rerender on any open/minimize/close
    const [show_more, setShowMore] = useState(false);
    const more_el_ref = useRef(null);
    const viewport_width = ViewPort?.WIDTH || window.innerWidth;

    useEffect(() => {fitMaximizedChatWindows(viewport_width)}, [viewport_width]);
    useEffect(
        () => {
            if(!show_more) return;
            const onDocClick = (evt) => {!more_el_ref.current?.contains(evt.target) && setShowMore(false)};
            document.addEventListener("mousedown", onDocClick);
            return () => document.removeEventListener("mousedown", onDocClick);
        }, [show_more]
    );

    const entries = Array.from(minimized_chat_windows.values());
    if(!entries.length) return null;

    const IS_LAGRGE_SCREEN = viewport_width > 768;
    const available_width = viewport_width - openChatWindowsWidth() - 16;
    const visible_count = !IS_LAGRGE_SCREEN ? 0
        : entries.length * CHAT_MINIMIZED_BAR_WIDTH <= available_width ? entries.length
        : Math.max(0, Math.floor((available_width - CHAT_MORE_BUTTON_WIDTH) / CHAT_MINIMIZED_BAR_WIDTH));
    /* most recently minimized stay visible, closest to open windows */
    const overflow_entries = entries.slice(0, entries.length - visible_count).reverse();
    const overflow_unseen = overflow_entries.reduce((total, entry) => total + entry.unseen_count, 0);

    return (
        <div className={`chat-min-container ${IS_LAGRGE_SCREEN ? "" : "chat-min-container-mobile"}`}>
            {
                overflow_entries.length
                ?   <div className='chat-min-more-wrapper' ref={more_el_ref}>
                        <div className='chat-min-more' onClick={() => setShowMore(!show_more)}>
                            {visible_count ? `+${overflow_entries.length} more` : `${overflow_entries.length} chats`}
                            {overflow_unseen ? <div className='chat-min-badge'>{overflow_unseen}</div> : null}
                            <ChevronUp size={16} style={show_more ? {transform: "rotate(180deg)"} : null} />
                        </div>
                        {
                            show_more
                            ?   <div className='chat-min-menu' onClick={() => setShowMore(false)}>
                                    <div className='chat-min-menu-header'>
                                        <span>Minimized chats</span>
                                        <span className='chat-min-menu-close-all'
                                            onClick={() => overflow_entries.forEach((entry) => entry.close())}
                                        >
                                            Close all
                                        </span>
                                    </div>
                                    <div className='chat-min-menu-list'>
                                        {overflow_entries.map((entry) => (
                                            <MinimizedChatItem entry={entry} className='chat-min-menu-item' key={entry.window_id} />
                                        ))}
                                    </div>
                                </div>
                            :   null
                        }
                    </div>
                :   null
            }
            {entries.slice(entries.length - visible_count).map((entry) => (
                <MinimizedChatItem entry={entry} key={entry.window_id}
                    className={`chat-min-bar ${getBarClassName?.() || "w3-teal"}`}
                />
            ))}
        </div>
    );
}

/* fixed bottom-right container holding chat windows and the minimized chats bar */
function chatWindowsContainer(getBarClassName){
    const ctx = createPopupsContainer(null, "chat_windows");
    if(!ctx.styled){
        Object.assign(ctx.el.style, {
            position: "fixed", zIndex: "30", bottom: "0px", right: "0px",
            maxWidth: "100%", overscrollBehavior: "none",
        });
        ctx.el.classList = "chat-windows-container"
        ctx.add(CHAT_DOCK_VIEW_KEY, <MinimizedChatsBar getBarClassName={getBarClassName} />);
        ctx.styled = true;
    }
    return ctx;
}

export { chatWindowsContainer, chatWindowKeys, maximizeChatWindow, useChatWindowMinimized };
